/**
 * Event Filter Component
 * Handles filtering of events by audience, format, and facilitator
 */
class EventFilter {
    /**
     * @param {Object} options - Configuration options
     * @param {string} options.containerSelector - Selector for event container (e.g., '.groups-events', '.workshop-events')
     * @param {string} options.itemSelector - Selector for individual event items (e.g., '.group-group', '.workshop-group')
     * @param {string} options.visibleCountId - ID for visible count element
     * @param {string} options.totalCountId - ID for total count element
     */
    constructor(options) {
        this.container = document.querySelector(options.containerSelector);
        this.itemSelector = options.itemSelector;
        this.visibleCountEl = document.getElementById(options.visibleCountId);
        this.totalCountEl = document.getElementById(options.totalCountId);

        this.audienceFilter = document.getElementById('audience-filter');
        this.formatFilter = document.getElementById('format-filter');
        this.facilitatorFilter = document.getElementById('facilitator-filter');
        this.clearBtn = document.getElementById('clear-filters');

        this.toggleBtn = document.querySelector('.filter-toggle-btn');
        this.filterPanel = document.getElementById('event-filter-panel');
        this.activeBadge = document.querySelector('.filter-active-badge');
        this.quickAudienceToggles = Array.from(document.querySelectorAll('input[name="audience-quick-filter"]'));

        if (!this.container) {
            console.warn('Event filter: Container not found');
            return;
        }

        this.items = Array.from(this.container.querySelectorAll(this.itemSelector));
        this.init();
    }

    init() {
        // Set initial count
        this.updateCounts();

        // Add event listeners
        if (this.audienceFilter) {
            this.audienceFilter.addEventListener('change', () => this.applyFilters());
        }

        if (this.quickAudienceToggles.length > 0) {
            this.quickAudienceToggles.forEach(toggle => {
                toggle.addEventListener('change', () => {
                    if (this.audienceFilter) {
                        this.audienceFilter.value = toggle.value;
                    }
                    this.applyFilters();
                });
            });
        }

        if (this.formatFilter) {
            this.formatFilter.addEventListener('change', () => this.applyFilters());
        }

        if (this.facilitatorFilter) {
            this.facilitatorFilter.addEventListener('change', () => this.applyFilters());
        }

        if (this.clearBtn) {
            this.clearBtn.addEventListener('click', () => this.clearFilters());
        }

        // Toggle button
        if (this.toggleBtn && this.filterPanel) {
            this.toggleBtn.addEventListener('click', () => this.toggleFilterPanel());
        }

        // Pre-select filters from URL params (e.g. ?audience=Women+Only)
        const params = new URLSearchParams(window.location.search);
        const hasUrlParams = params.get('audience') || params.get('format') || params.get('facilitator');
        if (this.audienceFilter && params.get('audience')) {
            this.audienceFilter.value = params.get('audience');
        }
        if (this.formatFilter && params.get('format')) {
            this.formatFilter.value = params.get('format');
        }
        if (this.facilitatorFilter && params.get('facilitator')) {
            this.facilitatorFilter.value = params.get('facilitator');
        }

        // Auto-open filter panel if URL params are present
        if (hasUrlParams) {
            this.openFilterPanel();
        }

        this.syncQuickAudienceToggles();
        this.updateQuickAudienceVisibility();
        this.applyFilters();

        console.log('✅ Event filter initialized:', {
            items: this.items.length,
            container: this.container
        });
    }

    /**
     * Toggle the filter panel open/closed
     */
    toggleFilterPanel() {
        const isCollapsed = this.filterPanel.classList.contains('is-collapsed');
        if (isCollapsed) {
            this.openFilterPanel();
        } else {
            this.closeFilterPanel();
        }
    }

    openFilterPanel() {
        if (!this.filterPanel || !this.toggleBtn) return;
        this.filterPanel.classList.remove('is-collapsed');
        this.filterPanel.setAttribute('aria-hidden', 'false');
        this.toggleBtn.setAttribute('aria-expanded', 'true');
        this.updateQuickAudienceVisibility();
    }

    closeFilterPanel() {
        if (!this.filterPanel || !this.toggleBtn) return;
        this.filterPanel.classList.add('is-collapsed');
        this.filterPanel.setAttribute('aria-hidden', 'true');
        this.toggleBtn.setAttribute('aria-expanded', 'false');
        this.updateQuickAudienceVisibility();
    }

    updateQuickAudienceVisibility() {
        const quickToggleGroup = document.querySelector('.audience-quick-toggle');
        if (!quickToggleGroup || !this.filterPanel) return;

        const isExpanded = !this.filterPanel.classList.contains('is-collapsed');
        quickToggleGroup.hidden = isExpanded;
    }

    syncQuickAudienceToggles() {
        if (this.quickAudienceToggles.length === 0 || !this.audienceFilter) return;

        const selectedAudience = this.audienceFilter.value;
        let matchedToggle = false;

        this.quickAudienceToggles.forEach(toggle => {
            const shouldCheck = toggle.value === selectedAudience;
            toggle.checked = shouldCheck;
            matchedToggle = matchedToggle || shouldCheck;
        });

        if (!matchedToggle) {
            this.quickAudienceToggles.forEach(toggle => {
                toggle.checked = false;
            });
        }
    }

    /**
     * Update the active filter badge count on the toggle button
     */
    updateActiveBadge() {
        if (!this.activeBadge) return;
        let count = 0;
        if (this.audienceFilter && this.audienceFilter.value) count++;
        if (this.formatFilter && this.formatFilter.value) count++;
        if (this.facilitatorFilter && this.facilitatorFilter.value) count++;

        if (count > 0) {
            this.activeBadge.textContent = count;
            this.activeBadge.removeAttribute('hidden');
        } else {
            this.activeBadge.setAttribute('hidden', '');
        }
    }

    /**
     * Apply filters based on current selection
     */
    applyFilters() {
        const selectedAudience = this.audienceFilter ? this.audienceFilter.value : '';
        const selectedFormat = this.formatFilter ? this.formatFilter.value : '';
        const selectedFacilitator = this.facilitatorFilter ? this.facilitatorFilter.value : '';

        let visibleCount = 0;

        this.items.forEach(item => {
            const itemAudiences = item.dataset.audiences || '';
            const itemFormats = item.dataset.formats || '';
            const itemFacilitators = item.dataset.facilitators || '';

            let matchesAudience = true;
            let matchesFormat = true;
            let matchesFacilitator = true;

            // Check audience filter
            if (selectedAudience) {
                const audienceList = itemAudiences.split('|').filter(Boolean);
                matchesAudience = audienceList.includes(selectedAudience);
            }

            // Check format filter (case-insensitive)
            if (selectedFormat) {
                const formatList = itemFormats.split('|').filter(Boolean);
                matchesFormat = formatList.some(format =>
                    format.toLowerCase() === selectedFormat.toLowerCase()
                );
            }

            if (selectedFacilitator) {
                const facilitatorList = itemFacilitators.split('|').filter(Boolean);
                matchesFacilitator = facilitatorList.includes(selectedFacilitator);
            }

            // Show/hide item based on filters
            const shouldShow = matchesAudience && matchesFormat && matchesFacilitator;
            item.style.display = shouldShow ? '' : 'none';

            if (shouldShow) {
                visibleCount++;
            }
        });

        this.updateCounts(visibleCount);
        this.toggleNoResultsMessage(visibleCount === 0);
        this.updateSectionVisibility();
        this.updateActiveBadge();
        this.syncQuickAudienceToggles();
    }

    /**
     * Hide audience sections that have no visible items
     */
    updateSectionVisibility() {
        const sections = this.container.querySelectorAll('.audience-section');
        sections.forEach(section => {
            const sectionItems = section.querySelectorAll(this.itemSelector);
            const hasVisible = Array.from(sectionItems).some(item => item.style.display !== 'none');
            section.style.display = hasVisible ? '' : 'none';
        });
    }

    /**
     * Clear all filters
     */
    clearFilters() {
        if (this.audienceFilter) {
            this.audienceFilter.value = '';
        }

        if (this.formatFilter) {
            this.formatFilter.value = '';
        }

        if (this.facilitatorFilter) {
            this.facilitatorFilter.value = '';
        }

        this.applyFilters();
    }

    /**
     * Update visible and total counts
     */
    updateCounts(visibleCount = null) {
        const visible = visibleCount !== null ? visibleCount : this.items.length;
        const total = this.items.length;

        if (this.visibleCountEl) {
            this.visibleCountEl.textContent = visible;
        }

        if (this.totalCountEl) {
            this.totalCountEl.textContent = total;
        }
    }

    /**
     * Toggle no results message
     */
    toggleNoResultsMessage(show) {
        // Check if message already exists
        let noResultsMsg = this.container.querySelector('.filter-no-results');

        if (show && !noResultsMsg) {
            // Create and insert message
            noResultsMsg = document.createElement('div');
            noResultsMsg.className = 'filter-no-results';
            noResultsMsg.innerHTML = '<p>No events match the selected filters. Please try a different combination.</p>';
            this.container.insertBefore(noResultsMsg, this.container.firstChild);
        } else if (!show && noResultsMsg) {
            // Remove message
            noResultsMsg.remove();
        }
    }
}

export default EventFilter;
